import { useEffect, useRef, useState } from 'react';
import styles from './RoiNavigator.module.css';

const REGION_HINT =
    'Regions of interest (guidance only — not enforced). Use arrows or open the list to jump.';

const HOVER_CLOSE_MS = 120;

/**
 * Compact prev/next + jump list for regions of interest (guidance only).
 * Popover behavior matches ViewerTopBar item picker / instructions: show on hover,
 * click center to pin, Escape / outside click unpins, pick closes.
 *
 * @param {object} props
 * @param {Array<{ id: string, label: string }>} props.rois
 * @param {number} props.activeIndex - 0-based index into rois
 * @param {(index: number) => void} props.onSelectIndex
 * @param {boolean} [props.disabled]
 */
export default function RoiNavigator({ rois, activeIndex, onSelectIndex, disabled = false }) {
    const [listPinned, setListPinned] = useState(false);
    const [listHovered, setListHovered] = useState(false);
    const wrapRef = useRef(null);
    const hoverCloseTimerRef = useRef(null);
    const n = rois?.length ?? 0;
    const safeIndex = n ? Math.min(Math.max(activeIndex, 0), n - 1) : 0;
    const current = n ? rois[safeIndex] : null;

    const showList = listPinned || listHovered;

    function closeList() {
        setListPinned(false);
        setListHovered(false);
    }

    function handleWrapMouseEnter() {
        clearTimeout(hoverCloseTimerRef.current);
        if (!listPinned) setListHovered(true);
    }

    function handleWrapMouseLeave() {
        hoverCloseTimerRef.current = setTimeout(() => setListHovered(false), HOVER_CLOSE_MS);
    }

    function handleCenterClick() {
        if (listPinned) {
            closeList();
        } else {
            setListPinned(true);
            setListHovered(false);
        }
    }

    useEffect(() => () => clearTimeout(hoverCloseTimerRef.current), []);

    useEffect(() => {
        if (!listPinned) return undefined;
        function onKeyDown(e) {
            if (e.key === 'Escape') closeList();
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [listPinned]);

    useEffect(() => {
        if (!listPinned) return undefined;
        function onMouseDown(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                closeList();
            }
        }
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [listPinned]);

    if (!n) return null;

    const go = (idx) => {
        if (disabled || n === 0) return;
        const j = ((idx % n) + n) % n;
        onSelectIndex?.(j);
        closeList();
    };

    const centerTitle = current?.label
        ? `${current.label} (${safeIndex + 1}/${n})`
        : `Region ${safeIndex + 1} (${safeIndex + 1}/${n})`;

    return (
        <div
            className={styles.wrap}
            ref={wrapRef}
            role="region"
            aria-label={REGION_HINT}
            title={REGION_HINT}
            onMouseEnter={handleWrapMouseEnter}
            onMouseLeave={handleWrapMouseLeave}
        >
            <button
                type="button"
                className={styles.arrowBtn}
                disabled={disabled || n <= 1}
                onClick={() => go(safeIndex - 1)}
                aria-label="Previous region"
            >
                ‹
            </button>
            <div className={styles.indexWrap}>
                <button
                    type="button"
                    className={[
                        styles.indexButton,
                        listPinned ? styles.indexButtonPinned : '',
                    ].filter(Boolean).join(' ')}
                    disabled={disabled}
                    onClick={handleCenterClick}
                    aria-expanded={showList}
                    aria-haspopup="listbox"
                    aria-label={`${centerTitle}. Hover or click to open jump list.`}
                    title={centerTitle}
                >
                    {safeIndex + 1}/{n}
                </button>
                {showList && (
                    <div className={styles.popover} role="listbox" aria-label="Jump to region">
                        <div className={styles.popoverTitle}>Jump to region</div>
                        {rois.map((r, i) => (
                            <button
                                key={r.id || String(i)}
                                type="button"
                                role="option"
                                aria-selected={i === safeIndex}
                                className={[
                                    styles.popoverItem,
                                    i === safeIndex ? styles.popoverItemActive : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => go(i)}
                                title={r.label ?? `Region ${i + 1}`}
                            >
                                {r.label ?? `Region ${i + 1}`}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <button
                type="button"
                className={styles.arrowBtn}
                disabled={disabled || n <= 1}
                onClick={() => go(safeIndex + 1)}
                aria-label="Next region"
            >
                ›
            </button>
        </div>
    );
}
